import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { UserRole } from '@/types'
import { rememberedView, rememberView, viewQuery } from './viewMemory'
import MainLayout from '@/layouts/MainLayout.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/Login.vue'),
    meta: { guest: true }
  },
  {
    // NEW: Callback route for Auth0 OAuth redirect
    path: '/callback',
    name: 'Callback',
    component: () => import('@/pages/Callback.vue'),
    meta: { guest: true }
  },
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: () => import('@/pages/Unauthorized.vue'),
    // `public`, not `guest`: guest means "bounce authenticated users to the
    // Dashboard", which is right for /login but would make this page — whose
    // audience is authenticated users without permissions — unreachable, and
    // loop against the redirect below at line ~197.
    meta: { public: true }
  },
  {
    path: '/design-demo',
    name: 'DesignDemo',
    component: () => import('@/pages/DesignDemo.vue'),
    meta: { public: true }
  },
  {
    path: '/',
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/pages/Dashboard.vue')
      },
      {
        // Cosa e' cambiato nell'ultima raccolta: OPENSPEC_NOVITA.md
        path: 'novita',
        name: 'Novita',
        component: () => import('@/pages/Novita.vue'),
        meta: { requiresAuth: true, feature: 'resources' }
      },
      {
        path: 'services',
        name: 'Services',
        component: () => import('@/pages/Services.vue'),
        meta: { requiresAuth: true, feature: 'services', rememberView: true }
      },
      {
        path: 'services/new',
        name: 'NewService',
        component: () => import('@/pages/NewService.vue'),
        meta: { requiresAuth: true, requiresTechUser: true, feature: 'services' }
      },
      {
        path: 'services/:handle',
        name: 'ServiceDetail',
        component: () => import('@/pages/ServiceDetail.vue'),
        meta: { requiresAuth: true, feature: 'services' }
      },
      {
        path: 'service/:handle/assign',
        name: 'resource-assign',
        component: () => import('@/pages/ResourceAssign.vue'),
        meta: { requiresAuth: true, requiresTechUser: true, feature: 'assignments' }
      },
      {
        path: 'service/:handle/unassign',
        name: 'resource-unassign',
        component: () => import('@/pages/ResourceUnassign.vue'),
        meta: { requiresAuth: true, requiresTechUser: true, feature: 'assignments' }
      },
      {
        path: 'templates',
        name: 'Templates',
        component: () => import('@/pages/Templates.vue'),
        meta: { requiresAuth: true, feature: 'templates', rememberView: true }
      },
      {
        path: 'accounts',
        name: 'Accounts',
        component: () => import('@/pages/Accounts.vue'),
        meta: { requiresAuth: true, feature: 'customers', rememberView: true }
      },
      {
        path: 'accounts/:id',
        name: 'AccountDetail',
        component: () => import('@/pages/AccountDetail.vue'),
        meta: { requiresAuth: true, feature: 'customers' }
      },
      {
        path: 'contracts',
        name: 'Contracts',
        component: () => import('@/pages/Contracts.vue'),
        meta: { requiresAuth: true, feature: 'contracts', rememberView: true }
      },
      {
        path: 'contracts/:code(.*)',
        name: 'ContractDetail',
        component: () => import('@/pages/ContractDetail.vue'),
        meta: { requiresAuth: true, feature: 'contracts' }
      },
      {
        path: 'customers',
        redirect: '/accounts'
      },
      {
        path: 'customers/:id',
        redirect: to => `/accounts/${to.params.id}`
      },
      {
        // I segnalibri e i link gia' scambiati puntano qui: la fatturazione
        // era una sola e non aveva bisogno di dire quale.
        path: 'billing',
        redirect: '/billing/infragruppo'
      },
      {
        path: 'billing/infragruppo',
        name: 'BillingInfragruppo',
        component: () => import('@/pages/Billing.vue'),
        meta: { requiresAuth: true, feature: 'billing', rememberView: true }
      },
      {
        path: 'billing/clienti',
        name: 'BillingClienti',
        component: () => import('@/pages/BillingClienti.vue'),
        meta: { requiresAuth: true, feature: 'billing', rememberView: true }
      },
      // I link scambiati quando la pagina si chiamava "a consumo" non cadono.
      { path: 'billing/consumo', redirect: '/billing/clienti' },
      {
        path: 'billing/listino',
        name: 'PriceList',
        component: () => import('@/pages/PriceList.vue'),
        meta: { requiresAuth: true, feature: 'billing', rememberView: true }
      },
      {
        path: 'billing/cataloghi',
        name: 'Cataloghi',
        component: () => import('@/pages/Cataloghi.vue'),
        meta: { requiresAuth: true, feature: 'billing', rememberView: true }
      },
      {
        path: 'admin',
        name: 'AdminUsers',
        component: () => import('@/pages/AdminUsers.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, feature: 'users', rememberView: true }
      },
      {
        path: 'audit-logs',
        name: 'AdminAuditLogs',
        component: () => import('@/pages/AdminAuditLogs.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, feature: 'users', rememberView: true }
      },
      {
        path: 'sessions',
        name: 'AdminSessions',
        component: () => import('@/pages/AdminSessions.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, feature: 'users', rememberView: true }
      },
      {
        path: 'roles',
        name: 'AdminRoles',
        component: () => import('@/pages/AdminRoles.vue'),
        meta: { requiresAuth: true, requiresAdmin: true, feature: 'users', rememberView: true }
      },
      {
        path: 'resources',
        name: 'Resources',
        component: () => import('@/pages/Resources.vue'),
        meta: { requiresAuth: true, feature: 'resources', rememberView: true }
      },
      {
        // Le risorse raggruppate per macchina (server_id): OPENSPEC_ISTANZE.md
        path: 'instances',
        name: 'Instances',
        component: () => import('@/pages/Instances.vue'),
        meta: { requiresAuth: true, feature: 'resources', rememberView: true }
      },
      {
        path: 'resource-types',
        name: 'ResourceTypes',
        component: () => import('@/pages/ResourceTypes.vue'),
        meta: { requiresAuth: true, feature: 'resource-types', rememberView: true }
      },
      {
        // Non "/apitool": il proxy (Vite in sviluppo, nginx in produzione)
        // inoltra al backend tutto cio' che comincia con /api.
        path: 'raccolta-dati',
        name: 'ApiToolProfili',
        component: () => import('@/pages/ApiToolProfili.vue'),
        meta: { requiresAuth: true, feature: 'apitool' }
      },
      {
        path: 'resources/:id',
        name: 'ResourceDetail',
        component: () => import('@/pages/ResourceDetail.vue'),
        meta: { requiresAuth: true, feature: 'resources' }
      },
      {
        path: 'resources/:id/history',
        name: 'ResourceHistory',
        component: () => import('@/pages/ResourceHistory.vue'),
        meta: { requiresAuth: true, feature: 'resources' }
      },
      {
        path: 'assets',
        name: 'Assets',
        component: () => import('@/pages/Assets.vue'),
        meta: { requiresAuth: true, feature: 'services', rememberView: true }
      },
      {
        path: 'assets/:id',
        name: 'AssetDetail',
        component: () => import('@/pages/AssetDetail.vue'),
        meta: { requiresAuth: true, feature: 'services' }
      },
      {
        // No feature gate: presentation preferences belong to whoever is logged in.
        path: 'preferences',
        name: 'Preferences',
        component: () => import('@/pages/Preferences.vue'),
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// UPDATED: Global navigation guard with RBAC and disabled user checking
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // App.vue chiama checkAuth() in onMounted, che gira DOPO la prima navigazione:
  // su un link diretto la guardia decideva con l'utente ancora vuoto, e canAccess
  // rispondeva di no. Aspettarlo qui toglie la corsa, per ogni ruolo.
  if (!authStore.user) {
    await authStore.checkAuth()
  }

  // Public routes (including /unauthorized) - skip auth check
  if (to.meta.public || to.meta.guest) {
    // Guest routes: redirect to dashboard if already authenticated
    if (to.meta.guest && authStore.isAuthenticated) {
      return next({ name: 'Dashboard' })
    }
    return next()
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // Check authentication status using computed property (no API call needed)
    if (!authStore.isAuthenticated) {
      // Not authenticated, redirect to login with return URL
      return next({
        name: 'Login',
        query: { redirect: to.fullPath }
      })
    }

    // Check if user is disabled or has unauthorized role
    if (authStore.user?.is_disabled || authStore.user?.role === UserRole.UNAUTHORIZED || (authStore.user?.role as string) === 'unauthorized') {
      return next({ name: 'Unauthorized' })
    }

    // Check feature access
    if (to.meta.feature && !authStore.canAccess(to.meta.feature as any)) {
      return next({ name: 'Unauthorized' })
    }

    // Check role requirements (RBA-12)
    if (to.meta.requiresAdmin && !authStore.hasRole(UserRole.ADMIN)) {
      return next({ name: 'Unauthorized' })
    }

    // NEW: Check for tech_user role requirement
    if (to.meta.requiresTechUser && !authStore.hasAnyRole([UserRole.ADMIN, UserRole.TECH_USER])) {
      return next({ name: 'Unauthorized' })
    }

    // NEW: Check for bco_user role requirement
    if (to.meta.requiresBcoUser && !authStore.hasAnyRole([UserRole.ADMIN, UserRole.TECH_USER, UserRole.BCO_USER])) {
      return next({ name: 'Unauthorized' })
    }
  }

  // Entrata "nuda" in una lista (menu, "Torna a…"): si riprende l'ultima vista
  // della scheda. Un link con parametri propri vince e non viene toccato. Solo
  // arrivando da un'altra pagina: dentro la lista una query vuota e' la pagina
  // stessa che si azzera, e rimetterle i filtri annullerebbe "Azzera filtri".
  if (to.meta.rememberView && from.path !== to.path && !Object.keys(viewQuery(to.query)).length) {
    const saved = rememberedView(to.path)
    if (saved) return next({ path: to.path, query: { ...to.query, ...saved }, replace: true })
  }

  // Allow navigation
  return next()
})

// Ogni vista mostrata diventa l'ultima, anche quella vuota: dopo "Azzera filtri"
// si rientra azzerati.
router.afterEach((to) => {
  if (to.meta.rememberView) rememberView(to.path, to.query)
})

export default router
